import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
import { parseStatusColorConfig } from '../../utils/statusColor';

export interface Package {
  id: number;
  clientId: number;
  packageTypeId?: number;
  packageCategoryId?: number;
  packageStartDate?: string;
  packageEndDate?: string;
  packageDescr: string;
  packageMinutes?: number;
  isOpen?: boolean;
  totalInvoicedMinutes?: number;
  remainingMinutes?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
  };
  packageType?: {
    id: number;
    pst_descr?: string;
    pst_color?: string;
  };
  packageCategory?: {
    id: number;
    psc_descr?: string;
  };
  crm_packages_tasks?: Array<{
    id: number;
    taskId?: number | null;
    task?: {
      taskId: number;
      taskDescription?: string | null;
      taskDate?: string | null;
      taskInvoicedMin?: number | null;
      taskStatusDescr?: { ts_descr: string } | null;
    };
  }>;
}

export interface PackagesResponse {
  records: Package[];
  openedCount?: number;
}

export const usePackages = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<PackagesResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data?.records || [],
    packages: data?.records || [],
    total: data?.records?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const usePackage = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Package>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    package: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get package types for select dropdown
export const usePackageTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    packageTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((type) => ({
      value: String(type.id),
      label: type.pst_descr || `Type #${type.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(type.pst_color).backgroundColor },
      }),
    })),
  };
};

// Hook to get package categories for select dropdown
export const usePackageCategoriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-service-category',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    packageCategories: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((category) => ({
      value: String(category.id),
      label: category.psc_descr || `Category #${category.id}`,
    })),
  };
};

